/**
 * Composable for managing store initialization across the app
 *
 * This composable centralizes the logic for loading and initializing all stores
 * to ensure consistent data availability across all pages.
 */
import { useCategoriesStore } from '@/stores/category'
import { useTransactionsStore } from '@/stores/transaction'
import { usePlaidItemsStore } from '@/stores/plaidItem'
import { useProfilesStore } from '@/stores/profile'
import { LoadingType, useLoadingStore } from '@/stores/loading'
import { getCurrentYearAndMonth, getCurrentYearAndMonthNumbers } from '@/utils'

export const useStoreInitialization = () => {
  const categoriesStore = useCategoriesStore()
  const transactionsStore = useTransactionsStore()
  const plaidItemsStore = usePlaidItemsStore()
  const profilesStore = useProfilesStore()
  const loadingStore = useLoadingStore()

  const initializeStores = async () => {
    // Load from device storage first
    await Promise.all([
      categoriesStore.loadFromDeviceStorage(),
      transactionsStore.loadFromDeviceStorage(),
      plaidItemsStore.loadFromDeviceStorage(),
      profilesStore.loadFromDeviceStorage(),
    ])

    loadingStore.startRefreshData()
    try {
      const { year, formattedMonth } = getCurrentYearAndMonthNumbers()

      // Fetch fresh data from server
      await Promise.all([
        plaidItemsStore.getItems(false, { throwOnError: true }),
        categoriesStore.fetchCategories(undefined, { throwOnError: true }),
        profilesStore.fetchProfiles(),
        transactionsStore.getTransactionsForAllItems(
          year,
          formattedMonth,
          false,
          true,
          { throwOnError: true },
        ),
      ])
      loadingStore.finishRefreshData('success')
    } catch (error) {
      loadingStore.finishRefreshData('error')
      throw error
    }

    // Create monthly budget if needed, this should be handled server side but just in case theres a timezone issue
    if (
      !loadingStore.isLoading(LoadingType.FetchAllCategories) &&
      categoriesStore.categories.length > 0 &&
      !categoriesStore.checkIfMonthlyBudgetExists(getCurrentYearAndMonth())
    ) {
      await categoriesStore.createNewMonthlyBudgetForUser()
    }
  }

  return {
    initializeStores,
  }
}
